export const MEETING_TYPES = ['agm', 'egm', 'committee', 'emergency', 'other'] as const;
export const MEETING_MODES = ['offline', 'online', 'hybrid'] as const;
export const MEETING_STATUSES = ['draft', 'scheduled', 'completed', 'cancelled'] as const;

export type MeetingType = (typeof MEETING_TYPES)[number];
export type MeetingMode = (typeof MEETING_MODES)[number];
export type MeetingStatus = (typeof MEETING_STATUSES)[number];

/** UI labels. `value` is exactly what the API enum allows. */
export const MEETING_TYPE_OPTIONS: { value: MeetingType; label: string }[] = [
  { value: 'agm', label: 'Annual General Meeting' },
  { value: 'egm', label: 'Special / Extraordinary General Meeting' },
  { value: 'committee', label: 'Committee Meeting' },
  { value: 'emergency', label: 'Emergency Meeting' },
  { value: 'other', label: 'Other' },
];

export const MEETING_MODE_OPTIONS: { value: MeetingMode; label: string }[] = [
  { value: 'offline', label: 'In person' },
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
];

export const MEETING_STATUS_OPTIONS: { value: MeetingStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  agm: 'Annual General Meeting',
  egm: 'Special / Extraordinary General Meeting',
  committee: 'Committee Meeting',
  emergency: 'Emergency Meeting',
  other: 'Other',
};

export const MEETING_MODE_LABELS: Record<MeetingMode, string> = {
  offline: 'In person',
  online: 'Online',
  hybrid: 'Hybrid',
};

export const MEETING_STATUS_LABELS: Record<MeetingStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** Map any UI/legacy value to the API enum. */
export function toApiMeetingType(type?: string | null): MeetingType | undefined {
  if (!type) return undefined;
  if (type === 'special') return 'egm';
  return (MEETING_TYPES as readonly string[]).includes(type) ? (type as MeetingType) : undefined;
}

export function toApiMeetingMode(mode?: string | null): MeetingMode | undefined {
  if (!mode) return undefined;
  if (mode === 'in_person') return 'offline';
  return (MEETING_MODES as readonly string[]).includes(mode) ? (mode as MeetingMode) : undefined;
}

export function meetingTypeLabel(type?: string | null): string {
  if (!type) return '—';
  if (type === 'special') return MEETING_TYPE_LABELS.egm;
  const apiType = toApiMeetingType(type);
  return apiType ? MEETING_TYPE_LABELS[apiType] : type;
}

export function meetingModeLabel(mode?: string | null): string {
  if (!mode) return '—';
  if (mode === 'in_person') return MEETING_MODE_LABELS.offline;
  const apiMode = toApiMeetingMode(mode);
  return apiMode ? MEETING_MODE_LABELS[apiMode] : mode;
}
